<template>
    <!-- Events type event modal -->
    <CrudEventModal :event="crudEvent" v-model:is-open="openEventModal" />

    <!-- Events type protocol modal -->
    <ProtocolInformationModal :protocol="protocol ?? {}" :horse="horse ?? {}" v-model:is-open="openProtocolModal" />

    <!-- Events type program modal -->
    <ProgramInformationModal :program="program ?? {}" :horse="horse ?? {}" v-model:is-open="openProgramModal" />

    <!-- Ask to sync sensor modal -->
    <Modal halfModal :title="$t('form.sync-session-from-sensor-title')" v-model:is-open="openSyncModal">
        <Information>
            {{ $t('form.sync-session-from-sensor-explanation') }}
            <i>{{ $t('form.sync-session-from-sensor-explanation-italic') }}</i>
        </Information>
        <div class="mt-5 rounded-md shadow-2xs">
            <Button
                :action="
                    () => {
                        openSyncModal = false
                        $router.push({ name: 'sensors.index' })
                    }
                "
            >
                {{ $t('sensor.sync-session-from-sensor') }}
            </Button>
            <!-- <Button :to="{ name: 'sensors.index' }"> {{ $t('sensor.sync-session-from-sensor') }} </Button> -->
        </div>
    </Modal>

    <!-- Ask to clean sensor memory modal -->
    <Modal halfModal :title="$t('sensor.clear-memory')" v-model:is-open="openEraseModal">
        <Information>
            {{ $t('sensor.all-sessions-synced') }}
        </Information>
        <div class="mt-5 rounded-md shadow-2xs">
            <Button
                danger
                :action="
                    () => {
                        openEraseModal = false
                        $movepro.current.eraseMemory()
                    }
                "
            >
                {{ $t('sensor.clear-memory') }}
            </Button>
        </div>
    </Modal>

    <Modal halfModal :title="$t('heartrate.polar-recovery-title')" v-model:is-open="openPolarRecoveryModal">
        <Information>
            {{ polarRecoveryDescription }}
        </Information>
        <div class="text-text-darker flex items-start gap-2 px-1">
            <Checkbox v-model="polarRecoveryDeleteAfterSave" class="mt-0.5 shrink-0" />
            <div class="pt-0.5 text-sm leading-5">
                {{ $t('heartrate.polar-pending-exercise-delete-after-save') }}
            </div>
        </div>
        <div class="mt-5 flex flex-col gap-4">
            <Button :action="runPolarRecoveryAction" :enabled="!polarRecoveryBusy">
                {{ polarRecoveryActionLabel }}
            </Button>
            <Button
                secondary
                :action="
                    () => {
                        openPolarRecoveryModal = false
                    }
                "
                :enabled="!polarRecoveryBusy"
            >
                {{ $t('heartrate.polar-recovery-action-later') }}
            </Button>
        </div>
    </Modal>

    <Modal
        halfModal
        :title="$t('heartrate.polar-pending-exercise-title')"
        v-model:is-open="openPolarPendingExerciseModal"
        :disable-close="polarPendingExerciseBusy"
    >
        <Information>
            {{ polarPendingExerciseDescription }}
        </Information>
        <div class="text-text-darker flex items-start gap-2 px-1">
            <Checkbox v-model="polarPendingExerciseDeleteAfterSave" class="mt-0.5 shrink-0" />
            <div class="pt-0.5 text-sm leading-5">
                {{ $t('heartrate.polar-pending-exercise-delete-after-save') }}
            </div>
        </div>
        <Information v-if="showPolarPendingExerciseStreamOnlyInfo">
            {{ $t('heartrate.polar-pending-exercise-stream-only') }}
        </Information>
        <div class="mt-5 flex flex-col gap-4">
            <Button :action="savePendingPolarExercise" :enabled="!polarPendingExerciseBusy">
                {{ $t('heartrate.polar-pending-exercise-save') }}
            </Button>
            <Button
                v-if="showPolarPendingExerciseDeleteAction"
                danger
                :action="deletePendingPolarExerciseFromDevice"
                :enabled="!polarPendingExerciseBusy"
            >
                {{ $t('heartrate.polar-pending-exercise-delete-now') }}
            </Button>
            <Button secondary :action="cancelPendingPolarExercise" :enabled="!polarPendingExerciseBusy">
                {{ $t('heartrate.polar-recovery-action-later') }}
            </Button>
        </div>
    </Modal>

    <UnvalidatedSessionModal v-model:session="session" v-model:is-open="openUnvalidatedSessionModal" />

    <InteractiveTutorial />
</template>

<script setup>
import { ref, computed, watch, inject, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'

import useEvents from '@/lib/hooks/use-events'
import useSessions from '@/lib/hooks/use-sessions'
import useSensors from '@/lib/hooks/use-sensors'
import {
    clearCurrentPolarExerciseFromDevice,
    clearPolarRecoverySnapshot,
    getCurrentPolarExerciseInfo,
    importCurrentPolarExerciseToApp,
    isPolarRecoveryCompatibleWithCurrentHeartrate,
    markPolarPendingExerciseAutoHandled,
    notifyPolarCapturesChanged,
    polarPendingExercisePromptState,
    polarRecoveryState,
    queuePolarRecoverySnapshot,
    requestPolarPendingExercisePrompt,
    resetPolarPendingExerciseAutoHandled,
    resolvePolarPendingExercisePrompt,
    setPolarPendingExerciseDeleteAfterSave,
    shouldAutoPromptPolarPendingExercise,
    shouldOpenPolarRecoveryForCurrentHeartrate,
    stopAndImportCurrentPolarExercise,
} from '@/lib/helpers/polar.js'
import { canOpenSession, opensConfirmation } from '@/lib/helpers/session-access'

import { parseNullableId } from '@/lib/helpers/numeric'
import { format } from '@/lib/helpers/time'

import CrudEventModal from '@/components/modals/CrudEventModal.vue'
import ProtocolInformationModal from '@/components/modals/ProtocolInformationModal.vue'
import ProgramInformationModal from '@/components/modals/ProgramInformationModal.vue'
import UnvalidatedSessionModal from '@/components/modals/UnvalidatedSessionModal.vue'
import InteractiveTutorial from '@/components/interactive-tutorial/InteractiveTutorial.vue'
import Information from '@/components/Information.vue'
import Button from '@/components/buttons/Primary.vue'
import Modal from '@/components/modals/Modal.vue'
import Checkbox from '@/components/form/Checkbox.vue'

const movepro = inject('movepro')
const heartrate = inject('heartrate')

const store = useStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const { t, locale } = useI18n()
const { clickedEvent, getEmptyEvent } = useEvents()
const { prefetchSession } = useSessions()
const { createOrUpdateSensor } = useSensors()

const openEventModal = ref(false)
const openProgramModal = ref(false)
const openProtocolModal = ref(false)
const openSyncModal = ref(false)
const openEraseModal = ref(false)
const openUnvalidatedSessionModal = ref(false)
const openPolarRecoveryModal = ref(false)
const polarRecoveryBusy = ref(false)
const polarRecoveryDeleteAfterSave = ref(true)
const handlingStoppedLiveSession = ref(false)
const polarPendingExerciseBusy = ref(false)
const autoPromptingPendingPolarExercise = ref(false)
const autoErasePromptShownThisConnection = ref(false)
const previousAllFilesUploaded = ref(false)

const horse = ref(undefined)
const session = ref(undefined)
const protocol = ref(undefined)
const program = ref(undefined)

const crudEvent = ref(getEmptyEvent())

const polarRecoveryDescription = computed(() => {
    if (heartrate?.isRecording?.value) {
        return t('heartrate.polar-recovery-recording')
    }

    return t('heartrate.polar-recovery-file-ready')
})

const polarRecoveryActionLabel = computed(() =>
    heartrate?.isRecording?.value
        ? t('heartrate.polar-recovery-action-stop-import')
        : t('heartrate.polar-recovery-action-import')
)

const openPolarPendingExerciseModal = computed({
    get: () => polarPendingExercisePromptState.isOpen,
    set: (isOpen) => {
        if (!isOpen) {
            cancelPendingPolarExercise()
        }
    },
})

const polarPendingExerciseDeleteAfterSave = computed({
    get: () => polarPendingExercisePromptState.deleteAfterSave,
    set: (value) => setPolarPendingExerciseDeleteAfterSave(value),
})

const polarPendingExerciseDescription = computed(() =>
    heartrate?.isRecording?.value
        ? t('heartrate.polar-pending-exercise-recording-description')
        : t('heartrate.polar-pending-exercise-description')
)

const showPolarPendingExerciseStreamOnlyInfo = computed(
    () => polarPendingExercisePromptState.context === 'before-recording' && !polarPendingExerciseDeleteAfterSave.value
)
const showPolarPendingExerciseDeleteAction = computed(
    () => !heartrate?.isRecording?.value && !!heartrate?.isReadyToFetchRecord?.value
)

const getPolarRecoveryErrorMessage = (error) => {
    const errorCode = error?.message || error
    if (errorCode === 'polar-stop-failed') {
        return t('heartrate.polar-stop-failed')
    }
    if (errorCode === 'polar-import-missing') {
        return t('heartrate.polar-import-missing')
    }
    return error?.message || t('heartrate.polar-import-failed')
}

const logPolarModalDebug = (step, payload = {}) => {
    console.info('[PolarModalDebug]', step, payload)
}

const cancelPendingPolarExercise = () => {
    if (polarPendingExerciseBusy.value || !polarPendingExercisePromptState.isOpen) return

    resolvePolarPendingExercisePrompt({ action: 'cancel' })
}

const savePendingPolarExercise = async () => {
    if (polarPendingExerciseBusy.value || !polarPendingExercisePromptState.isOpen) return

    polarPendingExerciseBusy.value = true
    try {
        const action = heartrate?.isRecording?.value ? stopAndImportCurrentPolarExercise : importCurrentPolarExerciseToApp
        const { capture, created } = await action({ heartrate })
        const captureKey = capture?.captureKey || polarPendingExercisePromptState.captureKey || null
        if (!capture?.id || !captureKey) {
            throw new Error('polar-import-missing')
        }

        let deletedFromDevice = false
        if (polarPendingExerciseDeleteAfterSave.value) {
            deletedFromDevice = await clearCurrentPolarExerciseFromDevice({ heartrate, capture })
            if (!deletedFromDevice) {
                toast.add({
                    severity: 'warn',
                    summary: t('heartrate.polar-clear-failed'),
                    life: 3000,
                })
            }
        }

        toast.add({
            severity: 'success',
            summary: created ? t('heartrate.polar-import-saved') : t('heartrate.polar-import-updated'),
            life: 3000,
        })
        notifyPolarCapturesChanged()

        const shouldMarkHandled = deletedFromDevice
        if (shouldMarkHandled) {
            markPolarPendingExerciseAutoHandled(captureKey)
        }
        resolvePolarPendingExercisePrompt({
            action: 'saved',
            deleteAfterSave: deletedFromDevice,
        })
    } catch (error) {
        toast.add({
            severity: 'warn',
            summary: getPolarRecoveryErrorMessage(error),
            life: 3000,
        })
    } finally {
        polarPendingExerciseBusy.value = false
    }
}

const deletePendingPolarExerciseFromDevice = async () => {
    if (polarPendingExerciseBusy.value || !polarPendingExercisePromptState.isOpen) return

    polarPendingExerciseBusy.value = true
    try {
        const currentExercise = await getCurrentPolarExerciseInfo(heartrate)
        const captureKey = currentExercise?.captureKey || polarPendingExercisePromptState.captureKey || null
        const deleted = await clearCurrentPolarExerciseFromDevice({
            heartrate,
            capture: currentExercise?.capture || null,
        })

        if (!deleted) {
            toast.add({
                severity: 'warn',
                summary: t('heartrate.polar-clear-failed'),
                life: 3000,
            })
            return
        }

        markPolarPendingExerciseAutoHandled(captureKey)
        notifyPolarCapturesChanged()
        resolvePolarPendingExercisePrompt({ action: 'deleted', deleteAfterSave: true })
        toast.add({
            severity: 'success',
            summary: t('heartrate.polar-pending-exercise-delete-success'),
            life: 3000,
        })
    } catch (error) {
        toast.add({
            severity: 'warn',
            summary: error?.message || t('heartrate.polar-clear-failed'),
            life: 3000,
        })
    } finally {
        polarPendingExerciseBusy.value = false
    }
}

const queuePolarRecovery = (snapshot) => {
    logPolarModalDebug('queue-recovery', { snapshot })
    queuePolarRecoverySnapshot(snapshot)
}

const hasActiveLiveSession = () => !!movepro.current.liveSession?.startedAt

const tryOpenPolarRecoveryModal = () => {
    if (hasActiveLiveSession()) {
        return
    }

    if (polarRecoveryState.pending) {
        logPolarModalDebug('evaluate-recovery-open', {
            pending: polarRecoveryState.pending,
            recoveryModalOpen: openPolarRecoveryModal.value,
            pendingExerciseModalOpen: polarPendingExercisePromptState.isOpen,
            isConnected: heartrate?.isConnected?.value,
            isPolar: heartrate?.isPolar?.value,
            isRecording: heartrate?.isRecording?.value,
            isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
            currentDeviceId: heartrate?.lastConnectedId?.value || null,
            currentExercise: heartrate?.PSFTP?.value?.properties?.exercise || null,
        })
    }

    if (!polarRecoveryState.pending) {
        return
    }

    if (polarPendingExercisePromptState.isOpen) {
        return
    }

    if (!shouldOpenPolarRecoveryForCurrentHeartrate(heartrate)) {
        return
    }

    logPolarModalDebug('open-recovery-modal', { pending: polarRecoveryState.pending })
    openPolarRecoveryModal.value = true
}

const finalizeStoppedLiveSessionIfNeeded = async () => {
    if (handlingStoppedLiveSession.value) return

    const currentSensorSerialId = movepro.current.properties?.uuid || null
    if (!movepro.current.isStopped || !movepro.current.liveSession?.startedAt || !currentSensorSerialId) {
        return
    }
    if (!movepro.current.isLiveSessionOwnedBy?.(currentSensorSerialId)) {
        return
    }

    handlingStoppedLiveSession.value = true
    try {
        const snapshot = await movepro.current.finalizeLiveSessionAfterSensorStop({
            refreshFiles: async () => {
                try {
                    await movepro.current.getSessionsList()
                } catch (error) {
                    console.warn('[ModalsGroup] Failed to refresh sensor files before finalizing live session', error)
                }
            },
        })
        queuePolarRecovery(snapshot)
        tryOpenPolarRecoveryModal()
    } catch (error) {
        console.warn('[ModalsGroup] Failed to finalize stopped live session', error)
    } finally {
        handlingStoppedLiveSession.value = false
    }
}

const runPolarRecoveryAction = async () => {
    if (!polarRecoveryState.pending || polarRecoveryBusy.value) return

    polarRecoveryBusy.value = true
    try {
        const currentExercise = await getCurrentPolarExerciseInfo(heartrate)
        const captureKey = currentExercise?.captureKey || null
        logPolarModalDebug('run-recovery-start', {
            pending: polarRecoveryState.pending,
            currentExercise,
            deleteAfterSave: polarRecoveryDeleteAfterSave.value,
            isRecording: heartrate?.isRecording?.value,
            isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
        })
        const action = heartrate?.isRecording?.value ? stopAndImportCurrentPolarExercise : importCurrentPolarExerciseToApp
        const { capture, created } = await action({
            heartrate,
            recordingIntentId: polarRecoveryState.pending.recordingIntentId,
            startedAt: polarRecoveryState.pending.startedAt,
            endedAt: polarRecoveryState.pending.endedAt,
        })
        logPolarModalDebug('run-recovery-imported', {
            capture,
            created,
            isRecording: heartrate?.isRecording?.value,
            isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
            currentExercise: heartrate?.PSFTP?.value?.properties?.exercise || null,
        })

        toast.add({
            severity: 'success',
            summary: created ? t('heartrate.polar-import-saved') : t('heartrate.polar-import-updated'),
            life: 3000,
        })
        if (polarRecoveryDeleteAfterSave.value) {
            const deleted = await clearCurrentPolarExerciseFromDevice({ heartrate, capture })
            logPolarModalDebug('run-recovery-delete-after-save', {
                deleted,
                captureId: capture?.id || null,
                isRecording: heartrate?.isRecording?.value,
                isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
                currentExercise: heartrate?.PSFTP?.value?.properties?.exercise || null,
            })
            if (!deleted) {
                toast.add({
                    severity: 'warn',
                    summary: t('heartrate.polar-clear-failed'),
                    life: 3000,
                })
            }
        }
        notifyPolarCapturesChanged()
        clearPolarRecoverySnapshot()
        openPolarRecoveryModal.value = false
        logPolarModalDebug('run-recovery-finished', {
            captureKey,
            isRecording: heartrate?.isRecording?.value,
            isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
            currentExercise: heartrate?.PSFTP?.value?.properties?.exercise || null,
        })
    } catch (error) {
        logPolarModalDebug('run-recovery-error', {
            error: error?.message || error,
            pending: polarRecoveryState.pending,
            isRecording: heartrate?.isRecording?.value,
            isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
            currentExercise: heartrate?.PSFTP?.value?.properties?.exercise || null,
        })
        toast.add({
            severity: 'warn',
            summary: getPolarRecoveryErrorMessage(error),
            life: 3000,
        })
    } finally {
        polarRecoveryBusy.value = false
    }
}

const maybePromptPendingPolarExerciseOnConnect = async () => {
    if (autoPromptingPendingPolarExercise.value || polarPendingExercisePromptState.isOpen) {
        return
    }

    if (hasActiveLiveSession()) {
        return
    }

    autoPromptingPendingPolarExercise.value = true
    try {
        if (openPolarRecoveryModal.value) {
            return
        }
        if (
            polarRecoveryState.pending &&
            isPolarRecoveryCompatibleWithCurrentHeartrate(heartrate) &&
            (heartrate?.isRecording?.value || heartrate?.isReadyToFetchRecord?.value)
        ) {
            return
        }
        if (!heartrate?.isConnected?.value || !heartrate?.isPolar?.value || heartrate?.isRecording?.value) {
            return
        }
        if (!heartrate?.isReadyToFetchRecord?.value) {
            return
        }
        if (movepro.current.isRecording || movepro.current.isPaused) {
            return
        }

        const currentExercise = await getCurrentPolarExerciseInfo(heartrate)
        const captureKey = currentExercise?.captureKey || null
        logPolarModalDebug('evaluate-pending-exercise-on-connect', {
            currentExercise,
            isRecording: heartrate?.isRecording?.value,
            isReadyToFetchRecord: heartrate?.isReadyToFetchRecord?.value,
            moveproRecording: movepro.current.isRecording,
            moveproPaused: movepro.current.isPaused,
        })
        if (!shouldAutoPromptPolarPendingExercise(captureKey)) {
            return
        }

        await requestPolarPendingExercisePrompt({
            context: 'connect',
            captureKey,
        })
    } catch (error) {
        console.warn('[ModalsGroup] Failed to prompt pending Polar exercise', error)
    } finally {
        autoPromptingPendingPolarExercise.value = false
    }
}

const openSessionPage = (sessionId) => {
    router.push({
        name: 'sessions.session',
        params: { sessionId: sessionId },
        query: { ...route.query },
    })
}

watch(
    () => clickedEvent.value,
    () => {
        if (clickedEvent.value) {
            switch (clickedEvent.value?.model) {
                case 'session':
                    if (
                        canOpenSession(clickedEvent.value?.session, {
                            isAdmin: !!store.state.auth?.user?.admin,
                        })
                    ) {
                        session.value = clickedEvent.value?.session
                        prefetchSession(session.value.id)
                        if (opensConfirmation(session.value)) {
                            openUnvalidatedSessionModal.value = true
                        } else {
                            openSessionPage(session.value.id)
                        }
                    }
                    break

                case 'protocol':
                    horse.value = clickedEvent.value?.horse
                    protocol.value = clickedEvent.value?.protocol
                    openProtocolModal.value = true
                    break

                case 'program':
                    horse.value = clickedEvent.value?.horse
                    program.value = clickedEvent.value?.program
                    openProgramModal.value = true
                    break

                // Events
                default:
                    crudEvent.value = clickedEvent.value
                    crudEvent.value.start_at = format(crudEvent.value.start_at, 'YYYY-MM-DD')
                    crudEvent.value.end_at = format(crudEvent.value.end_at, 'YYYY-MM-DD')
                    openEventModal.value = true
                    break
            }
            clickedEvent.value = undefined
        }
    }
)

watch(
    () => movepro.current.files,
    (files) => {
        const allFilesUploaded =
            movepro.current.isConnected &&
            files.length > 0 &&
            movepro.current.properties.filesCount === files.filter((f) => f.properties.uploaded === true).length

        if (allFilesUploaded && !previousAllFilesUploaded.value && !autoErasePromptShownThisConnection.value) {
            openEraseModal.value = true
            autoErasePromptShownThisConnection.value = true
        }

        previousAllFilesUploaded.value = allFilesUploaded
    },
    { deep: true }
)

let timerCheckup
let timerCheckupStartTimeout
const hasCurrentMoveProBlockingIssue = () =>
    movepro.current.properties.amsMode === 5 &&
    movepro.current.properties.gpsSpeed === 0 &&
    movepro.current.properties.gpsDistance === 0
const syncCurrentMoveProBlockingIssue = () => {
    movepro.current.setBlockingSensorIssue?.(hasCurrentMoveProBlockingIssue())
}

watch(
    () => movepro.current.isConnected,
    (isConnected, wasConnected) => {
        // console.log('isConnected, wasConnected', isConnected, wasConnected, movepro.current.isRecording)

        clearInterval(timerCheckup)
        clearTimeout(timerCheckupStartTimeout)

        if (isConnected && !wasConnected) {
            autoErasePromptShownThisConnection.value = false
            previousAllFilesUploaded.value = false
            openEraseModal.value = false
            movepro.current.clearBlockingSensorIssue?.()
            // Until the API answers for this sensor, never show the previous one's mounting.
            movepro.current.properties.default_orientation = null

            if (movepro.current.isRecording || movepro.current.isPaused) {
                // we do not redirect automatically to the live page because
                // we could go to the live page through the record button
                // console.log(
                //     'should redirect to record.live isConnected && !wasConnected movepro.current.isRecording || movepro.current.isPaused'
                // )
                // router.push({ name: 'record.live' })
            } else if (movepro.current.properties.memory > 0) {
                if (!movepro.current.isMemoryMode && route.name !== 'sensors.index' && route.name !== 'record.live') {
                    openSyncModal.value = true
                }
            }

            timerCheckupStartTimeout = setTimeout(() => {
                syncCurrentMoveProBlockingIssue()
                timerCheckup = setInterval(() => {
                    syncCurrentMoveProBlockingIssue()
                }, 5000)
            }, 5000)

            setTimeout(async () => {
                const { data: sensorDataFromDatabase } = await createOrUpdateSensor(movepro.current.properties)

                if (sensorDataFromDatabase?.alias !== undefined && sensorDataFromDatabase?.alias !== null) {
                    movepro.current.properties.alias = sensorDataFromDatabase.alias
                }

                if (sensorDataFromDatabase?.horse_id !== undefined) {
                    movepro.current.properties.horse_id = parseNullableId(sensorDataFromDatabase.horse_id)
                    if (movepro.current.properties.horse_id === null && sensorDataFromDatabase?.horse === undefined) {
                        movepro.current.properties.horse = null
                    }
                }

                if (sensorDataFromDatabase?.rider_id !== undefined) {
                    movepro.current.properties.rider_id = parseNullableId(sensorDataFromDatabase.rider_id)
                    if (movepro.current.properties.rider_id === null && sensorDataFromDatabase?.rider === undefined) {
                        movepro.current.properties.rider = null
                    }
                }

                if (sensorDataFromDatabase?.horse !== undefined) {
                    movepro.current.properties.horse = sensorDataFromDatabase.horse
                }

                if (sensorDataFromDatabase?.rider !== undefined) {
                    movepro.current.properties.rider = sensorDataFromDatabase.rider
                }

                if (sensorDataFromDatabase?.needs_update !== undefined) {
                    movepro.current.properties.needs_update = sensorDataFromDatabase.needs_update
                    movepro.current.properties.latest_version = sensorDataFromDatabase.latest_version
                    movepro.current.properties.firmware_download_url = sensorDataFromDatabase.firmware_download_url
                    movepro.current.properties.checksum = sensorDataFromDatabase.checksum
                }

                movepro.current.properties.canEdit = !!sensorDataFromDatabase?.canEdit
                // Display only: createOrUpdateSensor sends named fields only, so it never travels back.
                movepro.current.properties.default_orientation = sensorDataFromDatabase?.default_orientation ?? null
            }, 200)

            finalizeStoppedLiveSessionIfNeeded()
        } else if (!isConnected && wasConnected) {
            autoErasePromptShownThisConnection.value = false
            previousAllFilesUploaded.value = false
            openEraseModal.value = false
            movepro.current.clearBlockingSensorIssue?.()
            movepro.current.properties.default_orientation = null
        }
    }
)

onUnmounted(() => {
    clearInterval(timerCheckup)
    clearTimeout(timerCheckupStartTimeout)
})

watch(
    () => movepro.current.properties?.assStatus,
    () => {
        finalizeStoppedLiveSessionIfNeeded()
    }
)

watch(
    () => heartrate?.isConnected?.value,
    (isConnected, wasConnected) => {
        if (!isConnected && wasConnected) {
            resetPolarPendingExerciseAutoHandled()
        }
    }
)

watch(
    [() => heartrate?.isConnected?.value, () => heartrate?.isRecording?.value, () => heartrate?.isReadyToFetchRecord?.value],
    () => {
        if (!openPolarRecoveryModal.value) {
            tryOpenPolarRecoveryModal()
        }
        maybePromptPendingPolarExerciseOnConnect()
    }
)

watch(
    () => polarRecoveryState.pending,
    () => {
        if (!openPolarRecoveryModal.value) {
            tryOpenPolarRecoveryModal()
        }
    }
)

watch(
    () => session.value,
    (newSession) => {
        if (newSession?.validated) {
            openSessionPage(newSession.id)
        }
    }
)
</script>
